<template lang="pug">
  div
    b-loading(:active.sync="conversionInProgress")
    h2.has-text-weight-bold {{ $t('Convert an ecoSpold2 file Dataset(s) into a Dataset Excel Template') }}
    p.has-bottom-margin
      | {{ $t('You can convert up to') }}
      |
      | {{ maxUploads }}
      |
      | {{ $t('ecoSpold2 files.') }}
      |
      | {{ $t('Use the Dataset Excel Template to make desired changes and add new Datasets.') }}
      |
      | {{ $t('You can upload and validate the Dataset Excel Template via') }}
      |
      a(@click="$emit('toUploadTab')") Dataset Excel Template
        |
        |
        b-icon(size="is-small" icon="arrow-right")
        |
        | ecoSpold2
      |
      | {{ $t('tab and convert it back to ecoSpold2.') }}
    .columns
      .column
        b-field.file
          b-upload(v-model="files" :accept="mimeTypes" :disabled="uploadDisabled" multiple)
            a.button(:disabled="uploadDisabled" @click="clearErrors")
              b-icon(icon="upload")
              span {{ $t('Add EcoSpold Files') }}

      .column.has-text-centered-tablet
        a.button.is-green(v-if="showStart" :disabled="startDisabled"
        :class="{ 'is-loading': conversionInProgress }" @click="startConversion")
          b-icon(icon="chevron-circle-right")
          span {{ $t('Start Conversion') }}
        a.button.is-green(v-else-if="conversionSuccess" :href="resultUrl" :download="resultName")
          b-icon(icon="download")
          span
            slot {{ $t('Download Template') }}

      .column.has-text-right-tablet
        a.button.is-danger(@click="restart" :disabled="restartDisabled")
          b-icon(icon="undo")
          span {{ $t('Restart') }}
    div.has-bottom-margin.message.is-danger(v-if="conversionFailed")
      .message-body
        h3.has-text-weight-bold(v-for="error in errors") {{ error }}
    div.has-bottom-margin.message(v-if="conversionSuccess")
      .message-body
        h3.has-text-weight-bold {{ $t('The EcoSpold2 files were successfully converted to XLSX.') }}
    div(v-if="hasUploadedFiles")
      h3.has-text-weight-bold {{ $t('EcoSpold2 files to Convert') }}
      ul
        li(v-for="file in files") {{ file.name }}, {{ fileSize(file.size) }} kB, {{ fileDate(file.lastModified) }}
      p
</template>

<script>
import { CONTENT_TYPE_ECO_SPOLD2, uploadEcoSpold2, getEcoSpold2ConversionTime } from '../utils/api'
import ConvertDownloadButton from './ConvertDownloadButton'

const CONVERSION_NEW = 0
const CONVERSION_IN_PROGRESS = 1
const CONVERSION_SUCCESS = 2
const CONVERSION_FAILED = 4
const CONVERSION_RESET = 5

export default {
  name: 'EcoSpold2Upload',
  components: {
    ConvertDownloadButton
  },

  data() {
    return {
      mimeTypes: CONTENT_TYPE_ECO_SPOLD2,
      maxUploads: process.env.ECOINVENT_UPLOAD_MAX_SPOLD_FILES,
      conversionState: CONVERSION_NEW,
      files: [],
      errors: [],
      resultUrl: '',
      resultName: ''
    }
  },
  watch: {
    files(newArray, oldArray) {
      // watcher does not work on arrays. Even deep watching does not show the old array ("vue": "^2.5.2")
      // console.log(oldArray)
      if (newArray.length > this.maxUploads) {
        this.files = []
        this.$dialog.alert({
          title: 'Error',
          message: this.$t('You can upload at most') + ' ' + this.maxUploads + ' ' + this.$t('files') + '<br>' +
            this.$t('Please try again with less files.'),
          type: 'is-danger',
          hasIcon: true,
          icon: 'times-circle'
        })
      }
    }
  },
  computed: {
    conversionSuccess() {
      return this.conversionState === CONVERSION_SUCCESS
    },
    conversionFailed() {
      return this.conversionState === CONVERSION_FAILED
    },
    uploadDisabled() {
      return this.conversionState === CONVERSION_IN_PROGRESS ||
        this.conversionState === CONVERSION_SUCCESS ||
        this.conversionState === CONVERSION_FAILED ||
        this.uploadedFiles > this.maxUploads
    },
    startDisabled() {
      return this.conversionState === CONVERSION_IN_PROGRESS ||
        this.uploadedFiles > this.maxUploads ||
        this.uploadedFiles === 0
    },
    restartDisabled() {
      return this.uploadedFiles === 0 || this.conversionState === CONVERSION_IN_PROGRESS
    },
    showStart() {
      return this.conversionState === CONVERSION_NEW ||
        this.conversionState === CONVERSION_RESET ||
        this.conversionState === CONVERSION_IN_PROGRESS
    },
    conversionInProgress() {
      return this.conversionState === CONVERSION_IN_PROGRESS
    },
    uploadedFiles() {
      return this.files.length
    },
    hasUploadedFiles() {
      return !!this.uploadedFiles
    }
  },
  methods: {
    restart() {
      this.conversionState = CONVERSION_RESET
      this.files = []
      this.clearErrors()
    },
    fileSize(bytes) {
      return Number.parseInt(bytes / 1024)
    },
    fileDate(seconds) {
      return new Date(seconds).toLocaleString('en-US')
    },
    clearErrors() {
      this.errors = []
    },
    startConversion() {
      if (this.hasUploadedFiles) {
        this.conversionState = CONVERSION_IN_PROGRESS
        getEcoSpold2ConversionTime()
          .then((response) => {
            let expectedTime = Number(response.data.conversion_time__avg * 1000)
            expectedTime = expectedTime.toFixed()
            this.$Progress.start(expectedTime)
          })
        uploadEcoSpold2(this.files)
          .then((response) => {
            this.$Progress.finish()
            this.clearErrors()
            this.resultUrl = response.data.template_file
            this.resultName = this.resultUrl.split('/').pop()
            this.$emit('convertSpold2TemplateFinished', response)
            this.conversionState = CONVERSION_SUCCESS
          })
          .catch((error) => {
            this.$Progress.fail()
            this.conversionState = CONVERSION_FAILED
            if (error.response.status) {
              let childError = (
                Array.isArray(error.response.data) &&
                error.response.data[0] !== null &&
                typeof error.response.data[0] === 'object' &&
                error.response.data[0].hasOwnProperty('files') &&
                error.response.data[0].files.includes('child')
              )
              console.log('childError', childError)
              if (childError) {
                console.log('in child error')
                this.errors.push(this.$t('The EcoSpold2 could not be converted to XLSX. A file contains a child dataset, which cannot be handled by the converter.'))
              } else {
                console.log('not child error')
                this.errors.push(this.$t('The EcoSpold2 could not be converted to XLSX. Please check if the files are genuine.'))
              }
            }
          })
        this.$emit('convertSpold2Template', this.files)
      }
    }
  }
}
</script>

<style scoped>
  .has-bottom-margin {
    margin-bottom: 2rem;
  }
</style>



// WEBPACK FOOTER //
// src/components/EcoSpold2Upload.vue